import { on } from '@ngrx/store';
import { NotificationActions } from './notification.actions';
import { NotificationLoadingState } from './notification.state';
import { ApplicationStore } from '../application.state';

// **************************************************************************
// LOADING HELPER
// **************************************************************************

function withNotificationLoading(state: ApplicationStore, updates: Partial<NotificationLoadingState>): ApplicationStore {
    return {
        ...state,
        notification: {
            ...state.notification,
            loading: { ...state.notification.loading, ...updates },
        },
    };
}

// **************************************************************************
// NOTIFICATION REDUCER (nested under ApplicationStore)
// **************************************************************************

export const notificationReducer = [
    // =========================================================================
    // USER NOTIFICATIONS (Bell icon)
    // =========================================================================
    on(NotificationActions.loadUserNotificationsSuccess, (state: ApplicationStore, { unread, history }) => ({
        ...state,
        notification: {
            ...state.notification,
            unreadNotifications: unread,
            readStatuses: history,
            error: null,
        },
    })),

    on(NotificationActions.loadUserNotificationsFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // RELEASE NOTES
    // =========================================================================
    on(NotificationActions.loadReleaseNotesSuccess, (state: ApplicationStore, { notifications }) => ({
        ...state,
        notification: {
            ...state.notification,
            releaseNotes: notifications,
            error: null,
        },
    })),

    on(NotificationActions.loadReleaseNotesFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // ADMIN LIST
    // =========================================================================
    on(NotificationActions.loadAdminListSuccess, (state: ApplicationStore, { notifications }) => ({
        ...state,
        notification: {
            ...state.notification,
            adminList: notifications,
            error: null,
        },
    })),

    on(NotificationActions.loadAdminListFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // LOAD BY ID
    // =========================================================================
    on(NotificationActions.loadByIdSuccess, (state: ApplicationStore, { notification }) => ({
        ...state,
        notification: {
            ...state.notification,
            selectedNotification: notification,
            error: null,
        },
    })),

    on(NotificationActions.loadByIdFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // SELECTION (Bell icon)
    // =========================================================================
    on(NotificationActions.selectNotification, (state: ApplicationStore, { id, isUnread }) => {
        const notificationState = state.notification;
        // Find notification in unread or release notes
        const notification = isUnread
            ? notificationState.unreadNotifications.find(n => n.id === id)
            : notificationState.releaseNotes.find(n => n.id === id)
                ?? notificationState.readStatuses.find(r => r.notification?.id === id)?.notification;

        return {
            ...state,
            notification: {
                ...notificationState,
                selectedNotification: notification ?? null,
                isSelectedUnread: isUnread,
                showDialog: true,
                // Scope the reload to the dialog being opened now
                pendingAppReload: false,
            },
        };
    }),

    on(NotificationActions.clearSelection, (state: ApplicationStore) => ({
        ...state,
        notification: {
            ...state.notification,
            selectedNotification: null,
            isSelectedUnread: false,
            showDialog: false,
        },
    })),

    on(NotificationActions.showDialog, (state: ApplicationStore) => ({
        ...state,
        notification: { ...state.notification, showDialog: true },
    })),

    // =========================================================================
    // APP CACHE
    // =========================================================================
    on(NotificationActions.appCacheCleared, (state: ApplicationStore) => ({
        ...state,
        notification: { ...state.notification, pendingAppReload: true },
    })),

    on(NotificationActions.reloadApp, (state: ApplicationStore) => ({
        ...state,
        notification: { ...state.notification, pendingAppReload: false },
    })),

    // =========================================================================
    // MARK AS READ
    // =========================================================================
    on(NotificationActions.markAsReadSuccess, (state: ApplicationStore, { id, readStatus }) => {
        const notificationState = state.notification;
        // Find the notification before removing it from unread
        const notification = notificationState.unreadNotifications.find(n => n.id === id);

        // Ensure readStatus has the notification populated
        const enrichedReadStatus = readStatus.notification
            ? readStatus
            : { ...readStatus, notification };

        return {
            ...state,
            notification: {
                ...notificationState,
                unreadNotifications: notificationState.unreadNotifications.filter(n => n.id !== id),
                readStatuses: [enrichedReadStatus, ...notificationState.readStatuses],
                selectedNotification: null,
                isSelectedUnread: false,
                showDialog: false,
            },
        };
    }),

    // =========================================================================
    // CREATE
    // =========================================================================
    on(NotificationActions.createSuccess, (state: ApplicationStore, { notification }) => ({
        ...state,
        notification: {
            ...state.notification,
            adminList: [notification, ...state.notification.adminList],
            selectedNotification: null,
            error: null,
        },
    })),

    on(NotificationActions.createFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // UPDATE
    // =========================================================================
    on(NotificationActions.updateSuccess, (state: ApplicationStore, { notification }) => ({
        ...state,
        notification: {
            ...state.notification,
            adminList: state.notification.adminList.map(n => n.id === notification.id ? notification : n),
            error: null,
        },
    })),

    on(NotificationActions.updateFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // PUBLISH / UNPUBLISH
    // =========================================================================
    on(NotificationActions.publishSuccess, NotificationActions.unpublishSuccess, (state: ApplicationStore, { notification }) => ({
        ...state,
        notification: {
            ...state.notification,
            adminList: state.notification.adminList.map(n => n.id === notification.id ? notification : n),
            error: null,
        },
    })),

    on(NotificationActions.publishFailure, NotificationActions.unpublishFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // DELETE
    // =========================================================================
    on(NotificationActions.deleteSuccess, (state: ApplicationStore, { id }) => {
        const notificationState = state.notification;
        return {
            ...state,
            notification: {
                ...notificationState,
                adminList: notificationState.adminList.filter(n => n.id !== id),
                unreadNotifications: notificationState.unreadNotifications.filter(n => n.id !== id),
                releaseNotes: notificationState.releaseNotes.filter(n => n.id !== id),
                selectedNotification: notificationState.selectedNotification?.id === id ? null : notificationState.selectedNotification,
                error: null,
            },
        };
    }),

    on(NotificationActions.deleteFailure, (state: ApplicationStore, { error }) => ({
        ...state,
        notification: { ...state.notification, error },
    })),

    // =========================================================================
    // LOADING - USER NOTIFICATIONS
    // =========================================================================
    on(NotificationActions.loadUserNotifications, (state: ApplicationStore) => withNotificationLoading(state, { userNotifications: true })),
    on(NotificationActions.loadUserNotificationsSuccess, NotificationActions.loadUserNotificationsFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { userNotifications: false })),

    // =========================================================================
    // LOADING - RELEASE NOTES
    // =========================================================================
    on(NotificationActions.loadReleaseNotes, (state: ApplicationStore) => withNotificationLoading(state, { releaseNotes: true })),
    on(NotificationActions.loadReleaseNotesSuccess, NotificationActions.loadReleaseNotesFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { releaseNotes: false })),

    // =========================================================================
    // LOADING - ADMIN LIST
    // =========================================================================
    on(NotificationActions.loadAdminList, (state: ApplicationStore) => withNotificationLoading(state, { adminList: true })),
    on(NotificationActions.loadAdminListSuccess, NotificationActions.loadAdminListFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { adminList: false })),

    // =========================================================================
    // LOADING - BY ID
    // =========================================================================
    on(NotificationActions.loadById, (state: ApplicationStore, { id }) => withNotificationLoading(state, { byId: id })),
    on(NotificationActions.loadByIdSuccess, NotificationActions.loadByIdFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { byId: null })),

    // =========================================================================
    // LOADING - MARK AS READ
    // =========================================================================
    on(NotificationActions.markAsRead, (state: ApplicationStore, { id }) => withNotificationLoading(state, { markingAsRead: id })),
    on(NotificationActions.markAsReadSuccess, NotificationActions.markAsReadFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { markingAsRead: null })),

    // =========================================================================
    // LOADING - CREATE
    // =========================================================================
    on(NotificationActions.create, (state: ApplicationStore) => withNotificationLoading(state, { creating: true })),
    on(NotificationActions.createSuccess, NotificationActions.createFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { creating: false })),

    // =========================================================================
    // LOADING - UPDATE
    // =========================================================================
    on(NotificationActions.update, (state: ApplicationStore, { id }) => withNotificationLoading(state, { updating: id })),
    on(NotificationActions.updateSuccess, NotificationActions.updateFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { updating: null })),

    // =========================================================================
    // LOADING - PUBLISH/UNPUBLISH
    // =========================================================================
    on(NotificationActions.publish, NotificationActions.unpublish, (state: ApplicationStore, { id }) => withNotificationLoading(state, { publishing: id })),
    on(NotificationActions.publishSuccess, NotificationActions.unpublishSuccess,
        NotificationActions.publishFailure, NotificationActions.unpublishFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { publishing: null })),

    // =========================================================================
    // LOADING - DELETE
    // =========================================================================
    on(NotificationActions.delete, (state: ApplicationStore, { id }) => withNotificationLoading(state, { deleting: id })),
    on(NotificationActions.deleteSuccess, NotificationActions.deleteFailure,
        (state: ApplicationStore) => withNotificationLoading(state, { deleting: null })),
];
